import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { LOGISTICS_ORDERS_COLLECTION } from '@/modules/logistics/services/constants';
import type {
  LogisticsOrder,
  LogisticsOrderInput,
  LogisticsOrderStatus,
} from '@/modules/logistics/types';
import { publishLogisticsScheduled } from '@/services/events/eventPublisher.service';
import { recordLogisticsMovement } from '@/services/inventoryMovements';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type LogisticsOrderFirestoreDoc = {
  companyId: string;
  reservationId: string | null;
  type: LogisticsOrder['type'];
  status: LogisticsOrderStatus;
  scheduledDate: string;
  providerId: string | null;
  address: string;
  contactName: string;
  contactPhone: string;
  notes: string;
  inventoryItemIds: string[];
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapOrder(id: string, data: LogisticsOrderFirestoreDoc): LogisticsOrder {
  return {
    id,
    companyId: data.companyId,
    reservationId: data.reservationId,
    type: data.type,
    status: data.status,
    scheduledDate: data.scheduledDate,
    providerId: data.providerId,
    address: data.address ?? '',
    contactName: data.contactName ?? '',
    contactPhone: data.contactPhone ?? '',
    notes: data.notes ?? '',
    inventoryItemIds: data.inventoryItemIds ?? [],
    createdBy: data.createdBy,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listLogisticsOrders(
  companyId: string = appConfig.companyId,
): Promise<LogisticsOrder[]> {
  const q = query(
    collection(getFirestoreDb(), LOGISTICS_ORDERS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapOrder(d.id, d.data() as LogisticsOrderFirestoreDoc))
    .sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate));
}

export async function listLogisticsOrdersInRange(
  rangeStart: string,
  rangeEnd: string,
  companyId: string = appConfig.companyId,
): Promise<LogisticsOrder[]> {
  const q = query(
    collection(getFirestoreDb(), LOGISTICS_ORDERS_COLLECTION),
    where('companyId', '==', companyId),
    where('scheduledDate', '>=', rangeStart),
    where('scheduledDate', '<=', rangeEnd),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapOrder(d.id, d.data() as LogisticsOrderFirestoreDoc))
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}

export async function listLogisticsOrdersForReservation(
  reservationId: string,
): Promise<LogisticsOrder[]> {
  const all = await listLogisticsOrders();
  return all.filter((o) => o.reservationId === reservationId);
}

export async function getLogisticsOrder(
  id: string,
): Promise<LogisticsOrder | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), LOGISTICS_ORDERS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapOrder(snap.id, snap.data() as LogisticsOrderFirestoreDoc);
}

export async function saveLogisticsOrder(
  input: LogisticsOrderInput,
  createdBy: string,
  id?: string,
): Promise<LogisticsOrder> {
  return withFirestoreError(async () => {
  const ref = id
    ? doc(getFirestoreDb(), LOGISTICS_ORDERS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), LOGISTICS_ORDERS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(
        mapOrder(existing.id, existing.data() as LogisticsOrderFirestoreDoc),
      )
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    reservationId: input.reservationId,
    type: input.type,
    status: input.status,
    scheduledDate: input.scheduledDate,
    providerId: input.providerId,
    address: input.address.trim(),
    contactName: input.contactName.trim(),
    contactPhone: input.contactPhone.trim(),
    notes: input.notes.trim(),
    inventoryItemIds: input.inventoryItemIds,
    createdBy: existing.exists()
      ? (existing.data() as LogisticsOrderFirestoreDoc).createdBy
      : createdBy,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar la orden logística.');
  const result = mapOrder(saved.id, saved.data() as LogisticsOrderFirestoreDoc);
  const isCreate = !existing.exists();
  const becameScheduled =
    result.status === 'scheduled' &&
    (isCreate || before?.status !== 'scheduled');
  recordEntityWrite({
    entityType: 'logisticsOrder',
    entityId: result.id,
    isCreate,
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result),
  });
  if (becameScheduled) {
    void publishLogisticsScheduled({
      orderId: result.id,
      scheduledDate: result.scheduledDate,
      type: result.type,
    });
  }

  const prevStatus = before?.status as string | undefined;
  if (
    (prevStatus !== 'in_transit' && result.status === 'in_transit') ||
    (prevStatus !== 'delivered' &&
      result.status === 'delivered' &&
      result.type === 'delivery') ||
    (prevStatus !== 'returned' &&
      result.status === 'returned' &&
      result.type === 'pickup')
  ) {
    for (const itemId of result.inventoryItemIds) {
      void recordLogisticsMovement(
        itemId,
        result.id,
        result.type,
        result.createdBy,
      ).catch(() => undefined);
    }
  }

  return result;
  }, 'logisticsOrder');
}

export async function cancelLogisticsOrder(id: string): Promise<LogisticsOrder> {
  const existing = await getLogisticsOrder(id);
  if (!existing) throw new Error('Orden no encontrada.');
  return saveLogisticsOrder(
    {
      reservationId: existing.reservationId,
      type: existing.type,
      status: 'cancelled',
      scheduledDate: existing.scheduledDate,
      providerId: existing.providerId,
      address: existing.address,
      contactName: existing.contactName,
      contactPhone: existing.contactPhone,
      notes: existing.notes,
      inventoryItemIds: existing.inventoryItemIds,
    },
    existing.createdBy,
    id,
  );
}
